import { components as c } from "@/components";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { SurgeryCliniciansFormFields } from "@/features/events/features/surgeries/components/SurgeryCliniciansFormFields";
import { SurgeryFormFields } from "@/features/events/features/surgeries/components/SurgeryFormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSurgeryMutation } from "@/features/events/features/surgeries/api/upsertSurgery";
import { z } from "zod";
import { diaryByIdQueryOptions } from "@/features/diaries";
import { extractEventById } from "@/features/diaries/services/extractEventById";
import { SideZ } from "@patient-watch/types";
import { ui } from "@patient-watch/ui";
import { useState } from "react";
import { insertEvent } from "@/features/events/api/insertEvent";
import { updateDiaryFlowSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";

const searchSchema = updateDiaryFlowSearchSchema.extend({
    event_type: z.literal("surgery"),
});

const surgeryCliniciansZ = z.object({
    event_id: z.string().uuid(),
    surgery_id: z.string().uuid(),
    operating_clinician_id: z
        .string()
        .uuid()
        .nullable()
        .optional()
        .transform((value) => (value === "" ? null : value)),
    referring_clinician_id: z
        .string()
        .uuid()
        .nullable()
        .optional()
        .transform((value) => (value === "" ? null : value)),
});

const optionalSide = SideZ.nullable()
    .optional()
    .transform((value) => (value ? value : null));

const optionalSurgeryOptionId = z
    .string()
    .uuid()
    .or(z.literal(""))
    .nullable()
    .optional()
    .transform((value) => (value ? value : null));

const surgeryDetailsZ = z.object({
    event_id: z.string().uuid(),
    surgery_id: z.string().uuid(),
    side: optionalSide,
    surgery_option_id: optionalSurgeryOptionId,
    orthopaedic_area: z
        .string()
        .nullable()
        .optional()
        .transform((value) => (value?.trim() ? value : null)),
});

type SurgeryCliniciansT = z.infer<typeof surgeryCliniciansZ>;
type SurgeryDetailsT = z.infer<typeof surgeryDetailsZ>;
type Stage = "clinicians" | "details";

const AddSurgeryPage = () => {
    const { diaryId, eventId, trackerId } = Route.useParams();
    const search = Route.useSearch();
    const { type } = search;
    const { diary, surgery } = Route.useLoaderData();
    const navigate = useNavigate();
    const [stage, setStage] = useState<Stage>(() => (surgery ? "details" : "clinicians"));

    // Generate surgery ID
    const surgeryId = surgery?.surgery_id ?? crypto.randomUUID();

    const surgeryMutation = useSurgeryMutation({ diaryId });

    // Clinicians form
    const cliniciansForm = useForm<SurgeryCliniciansT>({
        resolver: zodResolver(surgeryCliniciansZ),
        defaultValues: {
            event_id: eventId,
            surgery_id: surgeryId,
            operating_clinician_id: surgery?.operating_clinician_id ?? search.operating_clinician_id ?? null,
            referring_clinician_id: surgery?.referring_clinician_id ?? search.referring_clinician_id ?? null,
        },
    });

    // Details form
    const detailsForm = useForm<SurgeryDetailsT>({
        resolver: zodResolver(surgeryDetailsZ),
        defaultValues: {
            surgery_id: surgeryId,
            event_id: eventId,
            side: surgery?.side ?? search.side ?? undefined,
            surgery_option_id: surgery?.surgery_option_id ?? search.surgery_option_id ?? undefined,
        },
    });

    const handleCliniciansSubmit: SubmitHandler<SurgeryCliniciansT> = async (formData) => {
        // First, ensure the event exists
        await insertEvent({ event_id: eventId, diary_id: diaryId });
        // Then insert the surgery
        surgeryMutation.mutate(formData, {
            onSuccess: () => setStage("details"),
        });
    };

    const handleDetailsSubmit: SubmitHandler<SurgeryDetailsT> = async (formData) => {
        surgeryMutation.mutate(formData, {
            onSuccess: () => {
                if (search.from === "worklist") {
                    void navigate({ to: "/worklist" });
                    return;
                }
                void navigate({
                    to: "/patients/$diaryId/update/$eventId/$trackerId/date",
                    params: { diaryId, eventId, trackerId },
                    search: { ...search, type },
                });
            },
        });
    };

    if (stage === "clinicians") {
        return (
            <c.ui.Form {...cliniciansForm}>
                <form onSubmit={cliniciansForm.handleSubmit(handleCliniciansSubmit)} className="space-y-6">
                    <h2 className="font-semibold text-lg">Surgery Clinicians</h2>
                    <SurgeryCliniciansFormFields organisationId={diary?.organisation_id as string} />
                    <fc.ContinueButton buttonType="submit" size="lg" className="w-full" />
                </form>
            </c.ui.Form>
        );
    }

    return (
        <ui.Form {...detailsForm}>
            <form onSubmit={detailsForm.handleSubmit(handleDetailsSubmit)} className="space-y-6">
                <h2 className="font-semibold text-lg">Surgery Details</h2>
                <SurgeryFormFields
                    sideFieldName="side"
                    orthopaedicAreaFieldName="orthopaedic_area"
                    surgeryOptionIdFieldName="surgery_option_id"
                />
                <fc.NextButton buttonType="submit" size="lg" className="w-full" />
            </form>
        </ui.Form>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/update/$eventId/$trackerId/intervention-details/surgery")(
    {
        component: AddSurgeryPage,
        validateSearch: searchSchema,
        loader: async ({ context, params }) => {
            const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
            const event = extractEventById(diary!, params.eventId);
            const surgery = event?.surgeries_view?.[0] ?? undefined;
            return { diary, event, surgery };
        },
    }
);
